import React, {createContext, useReducer, useContext} from 'react'
import moment from 'moment'

const LocaleStateContext = createContext()
const LocaleDispatchContext = createContext()

const initialState = {
  locale: 'en-US',
  language: 'en',
  strings: {}
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setLocale':
      return {
        ...state,
        locale: action.payload
      }

    case 'setStrings':
      return {
        ...state,
        strings: action.payload
      }
    case 'setData':
      return {
        ...state,
        ...action.payload
      }
  }
}

const processLocalizedStrings = () => {
  let essentialStrings = {}

  let strings = Ab.localization.Localization.localizedStrings
  if (strings) {
    essentialStrings = strings.reduce((acc, curVal) => {
      if (curVal.key2 === 'ab-essential-strings.js') {
        acc[curVal.key3] = curVal.value
      }
      return acc
    }, {})
  }

  return essentialStrings
}

const injectScript = ({url}) => {
  return new Promise(function(resolve, reject) {
    let head =
      document.getElementsByTagName('head')[0] || document.documentElement
    let script = document.createElement('script')
    script.async = true
    // onload fires even when script fails loads with an error.
    script.onload = resolve
    // onerror fires for malformed URLs.
    script.onerror = reject
    script.src = url
    head.appendChild(script)
  })
}

const fetchLanguageStrings = ({language}) => {
  let url = `../../../ab-core/controls/lang/ui-controls-lang-${language}.js`
  return injectScript({url}).then(() => {
    let essentialStrings = {}
    if (typeof Ab.localization !== 'undefined') {
      essentialStrings = processLocalizedStrings()
    }

    return Promise.resolve(essentialStrings)
  })
}

const getBrowserLocale = () => navigator.language || 'en_US'

/**
 * Returns the browser locale and the language code. The language code is used
 * to determine the language scripts to load from Web Central.
 * @returns {Object} locale - the browser locale, language the 2 character language string
 */
const getLocale = ({userInfo = null} = {}) => {
  let locale = getBrowserLocale()

  if (userInfo) {
    locale = userInfo.locale.replace('_', '-')
  }

  let language = locale
  if (locale.length > 1) {
    language = locale.substring(0, 2).toLowerCase()
    language = language === 'zh' ? 'ch' : language

    // Chrome browser returns nb for the Norwegian locale
    if (language === 'nb' || language === 'nn') {
      language = 'no'
    }
  } else {
    language = 'en'
  }

  return {locale, language}
}

const setUserLocale = ({userInfo = null, localeDispatch} = {}) => {
  const {language, locale} = getLocale({userInfo})

  fetchLanguageStrings({language}).then(strings => {
    moment.locale(language)
    localeDispatch({type: 'setData', payload: {language, locale, strings}})
  })
}

const _L = ({key, localeState}) => localeState.strings[key] || key

const LocaleProvider = ({children}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  return (
    <LocaleStateContext.Provider value={state}>
      <LocaleDispatchContext.Provider value={dispatch}>
        {children}
      </LocaleDispatchContext.Provider>
    </LocaleStateContext.Provider>
  )
}

const useLocaleState = () => {
  const context = useContext(LocaleStateContext)

  if (context === undefined) {
    throw new Error('useLocaleState must be used within a LocaleProvider')
  }
  return context
}

const useLocaleDispatch = () => {
  const context = useContext(LocaleDispatchContext)

  if (context === undefined) {
    throw new Error('useLocaleDispatch must be used within a LocaleProvider')
  }
  return context
}

export {LocaleProvider, useLocaleState, useLocaleDispatch, setUserLocale, _L}
